import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/LegalLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mailbox, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function OfflineComplaints() {
  useEffect(() => { document.title = "Offline Complaints Portal — Xurray"; }, []);
  const [form, setForm] = useState({ name: "", email: "", postal_address: "", subject: "", details: "" });
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/support/complaint", form);
      setRef(data.ref);
      toast.success("Your complaint has been logged.");
    } catch {
      toast.error("Could not submit your complaint. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Layout hideAd>
      <div data-testid="offline-complaints-page">
        <PageHero
          title="Offline Complaints Portal"
          eyebrow="Trust & Safety"
          icon={Mailbox}
          intro="Prefer not to use your account, or received an item purchased offline? Log a formal complaint here, or send it to us by post."
        />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="rounded-2xl border border-border/60 bg-muted/30 p-5 mb-8 text-sm" data-testid="postal-address">
            <div className="font-semibold mb-1">By post</div>
            <address className="not-italic text-muted-foreground leading-relaxed">
              Xurray Mart — Complaints Department<br />
              Compliance & Consumer Affairs<br />
              Postbus 1000, 1000 AA Amsterdam<br />
              The Netherlands
            </address>
            <p className="text-xs text-muted-foreground mt-2">Please include your contact details and any order reference.</p>
          </div>

          {ref ? (
            <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-8 animate-fade-up" data-testid="offline-success">
              <CheckCircle2 className="w-10 h-10 text-green-600 mb-3" />
              <h2 className="font-display font-bold text-xl">Complaint logged</h2>
              <p className="text-sm text-muted-foreground mt-1">Your reference number is <strong className="text-foreground" data-testid="offline-ref">{ref}</strong>. Our Consumer Affairs team will respond in writing.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8 space-y-5 animate-fade-up" data-testid="offline-form">
              <div><Label htmlFor="o-name">Full name</Label><Input id="o-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="offline-name" /></div>
              <div><Label htmlFor="o-email">Email</Label><Input id="o-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="offline-email" /></div>
              <div><Label htmlFor="o-addr">Postal address</Label><Textarea id="o-addr" required rows={3} value={form.postal_address} onChange={(e) => setForm({ ...form, postal_address: e.target.value })} className="mt-1.5" data-testid="offline-address" /></div>
              <div><Label htmlFor="o-subj">Subject</Label><Input id="o-subj" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" data-testid="offline-subject" /></div>
              <div><Label htmlFor="o-det">Complaint details</Label><Textarea id="o-det" required rows={6} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="mt-1.5" data-testid="offline-details" /></div>
              <Button type="submit" disabled={loading} size="lg" className="rounded-full w-full sm:w-auto" data-testid="offline-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit complaint"}
              </Button>
            </form>
          )}
        </main>
      </div>
    </Layout>
  );
}
